import {
  AlertCircle,
  Check,
  CheckCheck,
  Info,
  Maximize2,
  Settings2,
  X,
  XCircle,
} from 'lucide-react';
import { SECONDS_PER_DAY } from '@/lib/utils';
import type { ActiveDisasterRecoveryProposalResponse } from '@/hooks/admin/organization/use-active-disaster-recovery-proposal';
import type {
  ActiveDRSupportingProposal,
  DRConfig,
} from '@/hooks/admin/organization/use-disaster-recovery-settings';
import type { Signature } from '@/types/signature';
import { getOrgProposalSignatureCountsAndUserVote } from '@/utils/SignatureCountsHelpers';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { DRBadge } from '@/pages/settings/components/disaster-recovery-ui-utils';

/** Placeholder shown while disaster recovery settings data is loading. */
export function DRSettingsValueSkeleton() {
  return (
    <div className="flex flex-col gap-2 max-w-[565px]">
      <Skeleton className="h-7 w-48" />
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-4 w-full max-w-md" />
        <Skeleton className="h-4 w-3/4 max-w-sm" />
      </div>
    </div>
  );
}

interface DRSettingsValueProps {
  canManageDisasterRecovery: boolean;
  drConfig: DRConfig | null;
  drInitiationProposal: ActiveDisasterRecoveryProposalResponse;
  drCancellationProposal: ActiveDRSupportingProposal;
  drFinalizationProposal: ActiveDRSupportingProposal;
  currentUserId: string | null;
  onSetup: () => void;
  onReviewInitiationProposal: (userHasSigned: boolean) => void;
  onOpenCancelDialog: () => void;
  onOpenFinalizeDialog: () => void;
  onOpenCancellationProposalReview: () => void;
  onOpenFinalizationProposalReview: () => void;
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

interface DRConfigValueRowProps {
  label: string;
  value: string;
  containerClassName?: string;
  labelClassName?: string;
}

/**
 * Shared read-only label/value row used by both completed and in-card DR config
 * displays. Callers can override spacing/label width via class names.
 */
function DRConfigValueRow({
  label,
  value,
  containerClassName = 'flex items-center gap-1.5 text-xs',
  labelClassName = 'text-zinc-500 whitespace-nowrap',
}: DRConfigValueRowProps) {
  return (
    <div className={containerClassName}>
      <span className={labelClassName}>{label}</span>
      <code className="px-2 py-1 bg-muted rounded text-xs font-mono break-all">
        {value}
      </code>
    </div>
  );
}

function DRDisplayDivider() {
  return <div className="h-px w-full bg-zinc-200" />;
}

function formatTimelockFinalizeMessage(isoString: string): string {
  return `To finalize disaster recovery setup, return to this page after ${formatTimelockExpiry(isoString)}`;
}

type DRTimelockWaitCardVariant = 'timelock-wait' | 'finalization-ready';

interface DRTimelockWaitCardProps {
  variant?: DRTimelockWaitCardVariant;
  recoveryWalletAddress: string;
  timelockDurationSeconds: number;
  setupTimelockExpiresAt: string | null;
  showCancelButton: boolean;
  onOpenCancelDialog: () => void;
  showFinalizeButton?: boolean;
  onOpenFinalizeDialog?: () => void;
}

/**
 * Initiated-state card during timelock wait or finalization-ready (Figma Display / Alert).
 */
function DRTimelockWaitCard({
  variant = 'timelock-wait',
  recoveryWalletAddress,
  timelockDurationSeconds,
  setupTimelockExpiresAt,
  showCancelButton,
  onOpenCancelDialog,
  showFinalizeButton = false,
  onOpenFinalizeDialog,
}: DRTimelockWaitCardProps) {
  const isFinalizationReady = variant === 'finalization-ready';
  const headerMessage = isFinalizationReady
    ? 'Disaster recovery setup has been initiated and is ready to be finalized'
    : 'Disaster recovery setup has been initiated';
  const footerMessage = isFinalizationReady
    ? 'Disaster recovery setup must be finalized to take effect'
    : setupTimelockExpiresAt
      ? formatTimelockFinalizeMessage(setupTimelockExpiresAt)
      : null;
  const showFooterMessage = footerMessage !== null;
  const showActions = showFinalizeButton || showCancelButton;

  return (
    <div className="max-w-[565px] rounded-[10px] border border-orange-200 bg-orange-50 px-2.5 py-2.5 shadow-sm">
      <div className="flex gap-1.5 items-start pb-2">
        <Info className="w-3.5 h-3.5 text-orange-900 shrink-0 mt-0.5" />
        <p className="text-xs font-medium text-orange-900 leading-snug">
          {headerMessage}
        </p>
      </div>
      <div className="flex flex-col pl-5">
        <DRDisplayDivider />
        <div className="flex flex-col gap-2 py-3">
          <DRConfigValueRow
            label="Recovery wallet:"
            value={recoveryWalletAddress}
            containerClassName="flex items-center gap-4"
            labelClassName="w-[105px] shrink-0 text-xs leading-normal text-zinc-500 text-nowrap"
          />
          <DRConfigValueRow
            label="Timelock duration:"
            value={`${Math.round(timelockDurationSeconds / SECONDS_PER_DAY)} days`}
            containerClassName="flex items-center gap-4"
            labelClassName="w-[105px] shrink-0 text-xs leading-normal text-zinc-500 text-nowrap"
          />
        </div>
        {showFooterMessage ? (
          <>
            <DRDisplayDivider />
            <p className="py-3 text-xs font-medium leading-normal text-orange-900">
              {footerMessage}
            </p>
          </>
        ) : null}
        {showActions ? (
          <>
            <DRDisplayDivider />
            <div className="flex flex-wrap gap-1.5 pt-3">
              {showFinalizeButton && onOpenFinalizeDialog ? (
                <Button
                  type="button"
                  size="sm"
                  className="h-8 gap-1.5 rounded-md px-3 text-xs font-medium"
                  onClick={onOpenFinalizeDialog}
                >
                  <Check className="h-3.5 w-3.5 shrink-0" />
                  Finalize disaster recovery setup
                </Button>
              ) : null}
              {showCancelButton ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1.5 rounded-md bg-[#EF4444] px-3 text-xs font-medium text-white shadow-xs hover:bg-[#DC2626] hover:text-white"
                  onClick={onOpenCancelDialog}
                >
                  <X className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                  Cancel disaster recovery setup
                </Button>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

// DRBadge lives in a shared DR utils file so other DR surfaces can reuse it.

/** Formats a timelock expiry ISO string as a human-friendly date */
function formatTimelockExpiry(isoString: string | null): string {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

// ---------------------------------------------------------------------------
// Sub-cards
// ---------------------------------------------------------------------------

interface DRProposalCardProps {
  type: 'initiation' | 'cancellation' | 'finalization';
  approvalCount: number;
  rejectionsCount: number;
  requiredApprovals: number;
  userHasApproved: boolean;
  userHasRejected: boolean;
  isReadyToExecute: boolean;
  canReview: boolean;
  onReview: () => void;
}

/**
 * Orange proposal card used for initiation, cancellation, and finalization.
 * Adapts copy, button style, and icon by proposal type.
 */
function DRProposalCard({
  type,
  approvalCount,
  rejectionsCount,
  requiredApprovals,
  userHasApproved,
  userHasRejected,
  isReadyToExecute,
  canReview,
  onReview,
}: DRProposalCardProps) {
  const userHasSigned = userHasApproved || userHasRejected;
  const approvalsRemaining = Math.max(requiredApprovals - approvalCount, 0);
  const noun =
    type === 'initiation'
      ? 'initiate'
      : type === 'cancellation'
        ? 'cancel'
        : 'finalize';

  const titleText = isReadyToExecute
    ? `Proposal to ${noun} disaster recovery setup is ready to be executed`
    : userHasSigned
      ? `Proposal to ${noun} disaster recovery setup requires approval from ${approvalsRemaining} more admin${approvalsRemaining !== 1 ? 's' : ''}`
      : canReview
        ? `Proposal to ${noun} disaster recovery setup requires your approval`
        : `Proposal to ${noun} disaster recovery setup requires admin approval`;

  const reviewLabel =
    type === 'initiation'
      ? 'Review proposal to initiate disaster recovery setup'
      : `Review proposal to ${noun} disaster recovery setup`;

  return (
    <div className="bg-red-50 border border-orange-200 rounded-[10px] px-2.5 py-2.5 flex flex-col gap-1.5 max-w-lg">
      <div className="flex gap-1.5 items-start">
        <AlertCircle className="w-3.5 h-3.5 text-orange-900 shrink-0 mt-0.5" />
        <p className="text-xs font-medium text-orange-900 leading-snug">
          {titleText}
        </p>
      </div>
      <div className="flex gap-1.5 items-center flex-wrap">
        {isReadyToExecute ? (
          <DRBadge
            icon={AlertCircle}
            label="Needs to be executed"
            color="amber"
          />
        ) : userHasApproved ? (
          <DRBadge icon={Check} label="You approved" color="green" />
        ) : userHasRejected ? (
          <DRBadge icon={XCircle} label="You rejected" color="red" />
        ) : (
          <DRBadge
            icon={AlertCircle}
            label={canReview ? 'Needs your approval' : 'Needs admin approval'}
            color="amber"
          />
        )}
        {type === 'initiation' && userHasApproved && isReadyToExecute && (
          <DRBadge icon={Check} label="You approved" color="green" />
        )}
        <DRBadge
          icon={CheckCheck}
          label={`${approvalCount}/${requiredApprovals} Admin approvals`}
          color="green"
        />
        {/* Show rejection count once at least one admin has rejected. */}
        {rejectionsCount > 0 && (
          <DRBadge
            icon={XCircle}
            label={`${rejectionsCount}/${requiredApprovals} Rejections`}
            color="red"
          />
        )}
      </div>
      {/* Non-admins can view proposal status but cannot enter review/approval flows. */}
      {canReview ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onReview}
          className="h-auto p-0 justify-start text-xs font-medium text-zinc-700 hover:text-zinc-900 transition-colors mt-0.5 border border-zinc-200 rounded-md px-2.5 py-2 bg-white w-fit"
        >
          <Maximize2 className="w-3.5 h-3.5" />
          {reviewLabel}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Small status pill for disaster recovery not being setup
 */
function DisasterRecoveryNotSetupPill() {
  return (
    <div className="flex items-center gap-1.5 text-xs font-medium text-black border border-zinc-200 rounded-md px-2.5 py-2 bg-zinc-50 w-fit">
      <Info className="w-3.5 h-3.5 text-zinc-500" />
      Disaster recovery not setup
    </div>
  );
}

/**
 * Renders the value cell for the Disaster Recovery settings row.
 * Switches on the full DR lifecycle phase to show the appropriate status,
 * cards, and action buttons.
 */
export function DRSettingsValue({
  canManageDisasterRecovery,
  drConfig,
  drInitiationProposal,
  drCancellationProposal,
  drFinalizationProposal,
  currentUserId,
  onSetup,
  onReviewInitiationProposal,
  onOpenCancelDialog,
  onOpenFinalizeDialog,
  onOpenCancellationProposalReview,
  onOpenFinalizationProposalReview,
}: DRSettingsValueProps) {
  const userSignedOn = (
    approvals: Signature[] | undefined,
    rejections: Signature[] | undefined
  ) => {
    const { hasApproved, hasRejected } =
      getOrgProposalSignatureCountsAndUserVote({
        approvals: approvals ?? [],
        rejections: rejections ?? [],
        userId: currentUserId ?? undefined,
      });
    return { approved: hasApproved, rejected: hasRejected };
  };

  const completedApprovalCount = (approvals: Signature[] | undefined) =>
    getOrgProposalSignatureCountsAndUserVote({
      approvals: approvals ?? [],
      rejections: [],
      userId: undefined,
    }).numApprovals;

  const completedRejectionCount = (rejections: Signature[] | undefined) =>
    getOrgProposalSignatureCountsAndUserVote({
      approvals: [],
      rejections: rejections ?? [],
      userId: undefined,
    }).numRejections;

  // ---------------------------------------------------------------------------
  // Phase: COMPLETED — just show the configured details, no actions
  // ---------------------------------------------------------------------------
  if (drConfig?.phase === 'COMPLETED') {
    return (
      <div className="flex flex-col gap-1.5">
        <DRConfigValueRow
          label="Recovery wallet:"
          value={drConfig.recoveryWalletAddress}
        />
        <DRConfigValueRow
          label="Timelock duration:"
          value={`${Math.round(drConfig.timelockDurationSeconds / SECONDS_PER_DAY)} days`}
        />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Phase: FINALIZATION_READY — show finalize/cancel buttons + proposal cards
  // ---------------------------------------------------------------------------
  if (drConfig?.phase === 'FINALIZATION_READY') {
    const cancelSig = drCancellationProposal?.proposal
      ? userSignedOn(
          drCancellationProposal.approvals,
          drCancellationProposal.rejections
        )
      : null;
    const finSig = drFinalizationProposal?.proposal
      ? userSignedOn(
          drFinalizationProposal.approvals,
          drFinalizationProposal.rejections
        )
      : null;

    return (
      <div className="flex flex-col gap-2">
        <DisasterRecoveryNotSetupPill />

        <DRTimelockWaitCard
          variant="finalization-ready"
          recoveryWalletAddress={drConfig.recoveryWalletAddress}
          timelockDurationSeconds={drConfig.timelockDurationSeconds}
          setupTimelockExpiresAt={drConfig.setupTimelockExpiresAt}
          showFinalizeButton={
            canManageDisasterRecovery && !drFinalizationProposal?.proposal
          }
          onOpenFinalizeDialog={onOpenFinalizeDialog}
          showCancelButton={
            canManageDisasterRecovery && !drCancellationProposal?.proposal
          }
          onOpenCancelDialog={onOpenCancelDialog}
        />

        {/* Finalization proposal card */}
        {drFinalizationProposal?.proposal && finSig && (
          <DRProposalCard
            type="finalization"
            approvalCount={completedApprovalCount(
              drFinalizationProposal.approvals
            )}
            rejectionsCount={completedRejectionCount(
              drFinalizationProposal.rejections
            )}
            requiredApprovals={drFinalizationProposal.requiredApprovals}
            userHasApproved={finSig.approved}
            userHasRejected={finSig.rejected}
            isReadyToExecute={
              completedApprovalCount(drFinalizationProposal.approvals) >=
              drFinalizationProposal.requiredApprovals
            }
            canReview={canManageDisasterRecovery}
            onReview={onOpenFinalizationProposalReview}
          />
        )}

        {/* Cancellation proposal card */}
        {drCancellationProposal?.proposal && cancelSig && (
          <DRProposalCard
            type="cancellation"
            approvalCount={completedApprovalCount(
              drCancellationProposal.approvals
            )}
            rejectionsCount={completedRejectionCount(
              drCancellationProposal.rejections
            )}
            requiredApprovals={drCancellationProposal.requiredApprovals}
            userHasApproved={cancelSig.approved}
            userHasRejected={cancelSig.rejected}
            isReadyToExecute={
              completedApprovalCount(drCancellationProposal.approvals) >=
              drCancellationProposal.requiredApprovals
            }
            canReview={canManageDisasterRecovery}
            onReview={onOpenCancellationProposalReview}
          />
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Phase: TIMELOCK_WAIT — show initiated card + optional cancellation proposal
  // ---------------------------------------------------------------------------
  if (drConfig?.phase === 'TIMELOCK_WAIT') {
    const cancelSig = drCancellationProposal?.proposal
      ? userSignedOn(
          drCancellationProposal.approvals,
          drCancellationProposal.rejections
        )
      : null;

    return (
      <div className="flex flex-col gap-2">
        <DisasterRecoveryNotSetupPill />

        <DRTimelockWaitCard
          recoveryWalletAddress={drConfig.recoveryWalletAddress}
          timelockDurationSeconds={drConfig.timelockDurationSeconds}
          setupTimelockExpiresAt={drConfig.setupTimelockExpiresAt}
          showCancelButton={
            canManageDisasterRecovery && !drCancellationProposal
          }
          onOpenCancelDialog={onOpenCancelDialog}
        />

        {/* Cancellation proposal card */}
        {drCancellationProposal?.proposal && cancelSig && (
          <DRProposalCard
            type="cancellation"
            approvalCount={completedApprovalCount(
              drCancellationProposal.approvals
            )}
            rejectionsCount={completedRejectionCount(
              drCancellationProposal.rejections
            )}
            requiredApprovals={drCancellationProposal.requiredApprovals}
            userHasApproved={cancelSig.approved}
            userHasRejected={cancelSig.rejected}
            isReadyToExecute={
              completedApprovalCount(drCancellationProposal.approvals) >=
              drCancellationProposal.requiredApprovals
            }
            canReview={canManageDisasterRecovery}
            onReview={onOpenCancellationProposalReview}
          />
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Phase: CANCELLED — prior setup was cancelled; allow starting fresh
  // ---------------------------------------------------------------------------
  if (drConfig?.phase === 'CANCELLED') {
    return (
      <div className="flex flex-col gap-2">
        <DisasterRecoveryNotSetupPill />
        {canManageDisasterRecovery ? (
          <Button
            variant="outline"
            size="sm"
            onClick={onSetup}
            className="w-fit h-7 text-xs p-4"
          >
            <Settings2 className="h-4 w-4" />
            Setup disaster recovery
          </Button>
        ) : null}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Phase: NOT_STARTED — no initiation proposal yet
  // ---------------------------------------------------------------------------
  if (!drInitiationProposal?.proposal) {
    return (
      <div className="flex flex-col gap-2">
        <DisasterRecoveryNotSetupPill />
        {canManageDisasterRecovery ? (
          <Button
            variant="outline"
            size="sm"
            onClick={onSetup}
            className="w-fit h-7 text-xs p-4"
          >
            <Settings2 className="h-4 w-4" />
            Setup disaster recovery
          </Button>
        ) : null}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Phase: PENDING_INITIATION — initiation not executed on-chain yet (no drConfig)
  // ---------------------------------------------------------------------------
  if (!drConfig && drInitiationProposal.proposal) {
    const approvals = drInitiationProposal.approvals ?? [];
    const rejections = drInitiationProposal.rejections ?? [];
    const requiredApprovals = drInitiationProposal.requiredApprovals;
    const approvalCount = completedApprovalCount(approvals);
    const rejectionCount = completedRejectionCount(rejections);
    const isReadyToExecute =
      drInitiationProposal.proposal.status === 'PENDING' &&
      approvalCount >= requiredApprovals;
    const sig = userSignedOn(approvals, rejections);

    return (
      <div className="flex flex-col gap-2">
        <DisasterRecoveryNotSetupPill />
        <DRProposalCard
          type="initiation"
          approvalCount={approvalCount}
          rejectionsCount={rejectionCount}
          requiredApprovals={requiredApprovals}
          userHasApproved={sig.approved}
          userHasRejected={sig.rejected}
          isReadyToExecute={isReadyToExecute}
          canReview={canManageDisasterRecovery}
          onReview={() =>
            onReviewInitiationProposal(sig.approved || sig.rejected)
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <DisasterRecoveryNotSetupPill />
      {canManageDisasterRecovery ? (
        <Button
          variant="outline"
          size="sm"
          onClick={onSetup}
          className="w-fit h-7 text-xs p-4"
        >
          <Settings2 className="h-4 w-4" />
          Setup disaster recovery
        </Button>
      ) : null}
    </div>
  );
}
